export type WbrSection =
    | 'outcomes'
    | 'funnel'
    | 'paid'
    | 'email_outbound'
    | 'cold_calling'
    | 'events_referrals'
    | 'costs';

export type MetricUnit = 'usd' | 'count' | 'percent' | 'days';

/**
 * How the number is (or will be) measured. The card shows it, so every chart
 * answers "how is this measured?".
 */
export type MetricSource =
    /** Read from an existing dbt mart. */
    | Readonly<{ kind: 'modeled'; relation: string }>
    /** Read from exported data that has no mart yet. */
    | Readonly<{ kind: 'raw'; relation: string }>
    /** No data source exists yet. */
    | Readonly<{ kind: 'not_instrumented'; needs: string }>;

export type MetricDefinition = Readonly<{
    key: string;
    title: string;
    section: WbrSection;
    unit: MetricUnit;
    source: MetricSource;
    /** A known limit of the data, shown under the source. */
    note?: string;
}>;

/**
 * One period on a 6-12 chart. `null` means the period has no data and renders
 * as a line break or `N/A`, never as zero. Percent values are fractions.
 */
export type Point = Readonly<{
    /** ISO date (`YYYY-MM-DD`) the period starts on. */
    periodStart: string;
    value: number | null;
    priorYear: number | null;
    target: number | null;
}>;

/** The box-score row under a chart. Growth values are fractions. */
export type BoxScores = Readonly<{
    lastWeek: number | null;
    weekOverWeek: number | null;
    lastWeekYearOverYear: number | null;
    monthToDate: number | null;
    monthToDateYearOverYear: number | null;
    quarterToDate: number | null;
    quarterToDateYearOverYear: number | null;
    yearToDate: number | null;
    yearToDateYearOverYear: number | null;
}>;

/**
 * The week after the last completed one, from its Monday through `through`,
 * the metric's last complete day in it. Each comparison covers the same
 * weekdays, so a Monday–Thursday value meets Monday–Thursday values.
 */
export type PartialWeek = Readonly<{
    periodStart: string;
    through: string;
    value: number | null;
    sameDaysLastWeek: number | null;
    sameDaysLastYear: number | null;
}>;

export type MetricSeries = Readonly<{
    key: string;
    /** The six latest completed Monday–Sunday weeks, oldest first. */
    weeks: ReadonlyArray<Point>;
    /**
     * Null until the week in progress has a complete day. Box scores never
     * include it.
     */
    currentWeek: PartialWeek | null;
    /** The twelve latest completed calendar months, oldest first. */
    months: ReadonlyArray<Point>;
    boxScores: BoxScores;
}>;

const DEALS = { kind: 'raw', relation: 'stg_hubspot__deals' } as const;
const DEMO_MEETINGS = {
    kind: 'modeled',
    relation: 'int_sales__demo_meetings',
} as const;
const BOOKING_EVENTS = {
    kind: 'modeled',
    relation: 'mart_sales__booking_events',
} as const;
const INSTANTLY_EXPORT_GAP =
    'The Instantly export looks incomplete since mid-September 2026.';
const META_EXPERIMENT_LAUNCHES = {
    kind: 'modeled',
    relation: 'mart_marketing__meta_experiment_launches_weekly',
} as const;
const META_EFFICIENCY = {
    kind: 'modeled',
    relation: 'mart_marketing__meta_efficiency_daily',
} as const;
const TWILIO_BILL = {
    kind: 'modeled',
    relation: 'mart_wbr__daily_facts',
} as const;

/**
 * The revenue driver tree as a WBR deck: the outcomes, the funnel top-down,
 * then each channel's inputs and the fresh demos it books. Array order is deck order,
 * and a chart's number is its 1-based index here.
 */
export const WBR_METRICS = [
    {
        key: 'deals_won',
        title: 'Deals won',
        section: 'outcomes',
        unit: 'count',
        source: DEALS,
    },
    {
        key: 'thirty_day_close_rate',
        title: '30-day close rate',
        section: 'outcomes',
        unit: 'percent',
        source: DEALS,
    },
    {
        key: 'leads_created',
        title: 'Leads created',
        section: 'funnel',
        unit: 'count',
        source: { kind: 'raw', relation: 'stg_hubspot__leads' },
    },
    {
        key: 'lead_to_demo_rate',
        title: 'Lead → demo rate',
        section: 'funnel',
        unit: 'percent',
        source: {
            kind: 'raw',
            relation: 'stg_hubspot__leads + mart_sales__booking_events',
        },
    },
    {
        key: 'fresh_demos_booked',
        title: 'Fresh demos booked',
        section: 'funnel',
        unit: 'count',
        source: {
            kind: 'modeled',
            relation:
                'mart_sales__booking_events, with Attio history before 2026-08-31',
        },
    },
    {
        key: 'demo_show_rate',
        title: 'Demo show rate',
        section: 'funnel',
        unit: 'percent',
        source: DEMO_MEETINGS,
    },
    {
        key: 'demos_held',
        title: 'Demos held (initial and group)',
        section: 'funnel',
        unit: 'count',
        source: DEMO_MEETINGS,
    },
    {
        key: 'demo_to_deal_rate',
        title: 'Demo → deal rate',
        section: 'funnel',
        unit: 'percent',
        source: DEMO_MEETINGS,
    },
    {
        key: 'deals_entered',
        title: 'Deals entered',
        section: 'funnel',
        unit: 'count',
        source: DEALS,
    },
    {
        key: 'meta_creatives_launched',
        title: 'Meta creatives launched (Experiments campaign)',
        section: 'paid',
        unit: 'count',
        source: META_EXPERIMENT_LAUNCHES,
    },
    {
        key: 'meta_ad_sets_launched',
        title: 'Meta ad sets launched (Experiments campaign)',
        section: 'paid',
        unit: 'count',
        source: META_EXPERIMENT_LAUNCHES,
    },
    {
        key: 'meta_ad_spend',
        title: 'Meta ad spend',
        section: 'paid',
        unit: 'usd',
        source: META_EFFICIENCY,
    },
    {
        key: 'meta_cost_per_lead',
        title: 'Meta cost per lead (Meta-reported)',
        section: 'paid',
        unit: 'usd',
        source: META_EFFICIENCY,
    },
    {
        key: 'meta_cost_per_fresh_demo',
        title: 'Meta cost per fresh demo',
        section: 'paid',
        unit: 'usd',
        source: {
            kind: 'modeled',
            relation:
                'mart_marketing__meta_efficiency_daily + mart_sales__booking_events',
        },
    },
    {
        key: 'fresh_demos_meta',
        title: 'Meta fresh demos booked',
        section: 'paid',
        unit: 'count',
        source: BOOKING_EVENTS,
    },
    {
        key: 'fresh_demos_google_ads',
        title: 'Google Ads fresh demos booked',
        section: 'paid',
        unit: 'count',
        source: BOOKING_EVENTS,
    },
    {
        key: 'fresh_demos_webinar',
        title: 'Webinar fresh demos booked',
        section: 'paid',
        unit: 'count',
        source: BOOKING_EVENTS,
    },
    {
        key: 'fresh_demos_other_paid',
        title: 'Other paid fresh demos booked',
        section: 'paid',
        unit: 'count',
        source: BOOKING_EVENTS,
        note: 'Paid channels without their own card, such as ChatGPT Ads.',
    },
    {
        key: 'outbound_emails_sent',
        title: 'Outbound emails sent',
        section: 'email_outbound',
        unit: 'count',
        source: { kind: 'raw', relation: 'stg_core__instantly_messages' },
        note: INSTANTLY_EXPORT_GAP,
    },
    {
        key: 'outbound_email_reply_rate',
        title: 'Outbound reply rate',
        section: 'email_outbound',
        unit: 'percent',
        source: { kind: 'raw', relation: 'stg_core__instantly_replies' },
        note: INSTANTLY_EXPORT_GAP,
    },
    {
        key: 'outbound_replies_booked',
        title: 'Replies → booked',
        section: 'email_outbound',
        unit: 'count',
        source: BOOKING_EVENTS,
        note: INSTANTLY_EXPORT_GAP,
    },
    {
        key: 'fresh_demos_email_outbound',
        title: 'Email-outbound fresh demos booked',
        section: 'email_outbound',
        unit: 'count',
        source: BOOKING_EVENTS,
    },
    {
        key: 'cold_call_activity',
        title: 'Outbound prospecting calls',
        section: 'cold_calling',
        unit: 'count',
        source: {
            kind: 'modeled',
            relation: 'mart_sales__rep_lead_activity_daily',
        },
    },
    {
        key: 'fresh_demos_sales_outbound',
        title: 'Sales-outbound fresh demos booked',
        section: 'cold_calling',
        unit: 'count',
        source: BOOKING_EVENTS,
        note: "A rep link booked by someone who was never a Lead (Pure Outbound), whatever the booking's source.",
    },
    {
        key: 'events_referral_activity',
        title: 'Events and referral activity',
        section: 'events_referrals',
        unit: 'count',
        source: {
            kind: 'not_instrumented',
            needs: 'events attended and referral asks made, recorded somewhere Snowflake can read',
        },
    },
    {
        key: 'fresh_demos_events',
        title: 'Events fresh demos booked',
        section: 'events_referrals',
        unit: 'count',
        source: BOOKING_EVENTS,
    },
    {
        key: 'fresh_demos_referrals',
        title: 'Referral fresh demos booked',
        section: 'events_referrals',
        unit: 'count',
        source: BOOKING_EVENTS,
    },
    {
        key: 'twilio_spend',
        title: 'Twilio spend',
        section: 'costs',
        unit: 'usd',
        source: TWILIO_BILL,
    },
    {
        key: 'twilio_text_spend',
        title: 'Twilio text spend',
        section: 'costs',
        unit: 'usd',
        source: TWILIO_BILL,
    },
    {
        key: 'twilio_call_spend',
        title: 'Twilio call spend',
        section: 'costs',
        unit: 'usd',
        source: TWILIO_BILL,
    },
] as const satisfies ReadonlyArray<MetricDefinition>;

export type WbrMetric = (typeof WBR_METRICS)[number];

/** Keys of the metrics that have a data source, so they get a series. */
export type MeasuredMetricKey = Exclude<
    WbrMetric,
    Readonly<{ source: Readonly<{ kind: 'not_instrumented' }> }>
>['key'];

/** A function's own WBR holds every metric in its sections. */
export const WBR_FUNCTIONS = ['growth', 'finance'] as const;

export type WbrFunction = (typeof WBR_FUNCTIONS)[number];

export const WBR_FUNCTION_TITLES: Readonly<Record<WbrFunction, string>> = {
    growth: 'Growth',
    finance: 'Finance',
};

const SECTIONS: Readonly<
    Record<WbrSection, Readonly<{ title: string; owner: WbrFunction }>>
> = {
    outcomes: { title: 'Outcomes', owner: 'growth' },
    funnel: { title: 'Funnel', owner: 'growth' },
    paid: { title: 'Paid', owner: 'growth' },
    email_outbound: { title: 'Email outbound', owner: 'growth' },
    cold_calling: { title: 'Cold calling', owner: 'growth' },
    events_referrals: { title: 'Events and referrals', owner: 'growth' },
    costs: { title: 'Costs', owner: 'finance' },
};

/** The company deck, or one function's deck. */
export type WbrView = 'company' | WbrFunction;

export type NumberedMetric = Readonly<{
    number: number;
    metric: WbrMetric;
    /** Where the metric lives, such as `Growth · Funnel`. */
    home: string;
}>;

export type WbrDeckSection = Readonly<{
    key: string;
    /** Null on the company deck, which runs as one unheaded sequence. */
    title: string | null;
    metrics: ReadonlyArray<NumberedMetric>;
}>;

/**
 * The view's deck, numbered from 1. A function's deck is each run of
 * same-section metrics under its heading. The company deck is the starred
 * metrics in catalog order, so each function's driver order survives without
 * headings.
 */
export function wbrDeck(
    view: WbrView,
    companyMetricKeys: ReadonlySet<string>,
): ReadonlyArray<WbrDeckSection> {
    const metrics = WBR_METRICS.filter((metric) =>
        view === 'company'
            ? companyMetricKeys.has(metric.key)
            : SECTIONS[metric.section].owner === view,
    );
    const sections: Array<{
        key: string;
        title: string | null;
        metrics: Array<NumberedMetric>;
    }> = [];
    metrics.forEach((metric, index) => {
        const { title, owner } = SECTIONS[metric.section];
        const numbered = {
            number: index + 1,
            metric,
            home: `${WBR_FUNCTION_TITLES[owner]} · ${title}`,
        };
        const key = view === 'company' ? view : metric.section;
        const current = sections[sections.length - 1];
        if (current?.key === key) {
            current.metrics.push(numbered);
        } else {
            sections.push({
                key,
                title: view === 'company' ? null : title,
                metrics: [numbered],
            });
        }
    });
    return sections;
}
